import { useMemo, useState, type ReactElement } from "react";
import { ArrowDown, ArrowUp, Sparkles } from "lucide-react";
import type { KpiDef, KpiStatus } from "@playbookai/types";
import { EmptyState } from "../../molecules/EmptyState/index.js";
import { cn } from "../../lib/utils.js";
import { statusToken } from "../../lib/types.js";
import { Sparkline } from "../../atoms/Sparkline/Sparkline.js";
import { statusDeltaClass, statusToSparkTone } from "../kpi-spark-tone.js";
import type { WidgetBodyProps } from "../types.js";
import type { KpiTableView } from "./types.js";

type SortKey = "name" | "status" | "delta";

const STATUS_RANK: Record<KpiStatus, number> = {
  critical: 0,
  watch: 1,
  healthy: 2,
  none: 3,
};

const GRID = "grid grid-cols-[minmax(0,1fr)_84px_64px_56px] gap-2 items-center";

/** A one-line AI-style readout — counts KPIs moving the wrong way + the biggest mover. */
function buildSummary(rows: readonly KpiDef[]): string {
  if (rows.length === 0)
    return "No KPIs selected — choose KPIs in the edit dialog.";
  const down = rows.filter((k) =>
    k.inverse ? k.deltaNumeric > 0 : k.deltaNumeric < 0,
  );
  if (down.length === 0) {
    return `All ${rows.length} KPIs holding or improving — no movers worth flagging.`;
  }
  const biggest = [...down].sort(
    (a, b) => Math.abs(b.deltaNumeric) - Math.abs(a.deltaNumeric),
  )[0]!;
  return `${down.length} of ${rows.length} trending the wrong way — biggest mover: ${biggest.name}.`;
}

function compare(a: KpiDef, b: KpiDef, key: SortKey): number {
  if (key === "name") return a.name.localeCompare(b.name);
  if (key === "status") return STATUS_RANK[a.status] - STATUS_RANK[b.status];
  return a.deltaNumeric - b.deltaNumeric;
}

/**
 * KPI Table — dashboard data widget. Multi-KPI sortable table with a per-row
 * sparkline + an AI summary line. Data-blind: the host resolves the pinned ids;
 * membership is edited via the dialog's checklist editor. Sort is local view state.
 */
export function KpiTableWidget({
  data,
}: WidgetBodyProps<KpiTableView>): ReactElement {
  const [sortKey, setSortKey] = useState<SortKey>("status");
  const [asc, setAsc] = useState(true);

  const rows = useMemo(() => {
    const sorted = [...data.kpis].sort((a, b) => compare(a, b, sortKey));
    return asc ? sorted : sorted.reverse();
  }, [data.kpis, sortKey, asc]);

  const onSort = (key: SortKey): void => {
    if (key === sortKey) setAsc((v) => !v);
    else {
      setSortKey(key);
      setAsc(true);
    }
  };

  // A plain render-helper (not a component) — keeps sort state in this body
  // rather than a child remounted each render (react-hooks/static-components).
  const sortHeader = (
    k: SortKey,
    label: string,
    align: "start" | "end" = "start",
  ): ReactElement => (
    // `display:contents` span carries the columnheader role + aria-sort (a
    // <button> may not take role="columnheader" — aria-allowed-role); the button
    // stays the grid item, so layout is unchanged.
    <span
      role="columnheader"
      aria-sort={sortKey === k ? (asc ? "ascending" : "descending") : "none"}
      className="contents"
    >
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a sort header inside a CSS-grid row; Button would add a box and padding to the header cell, and no approved look row lists the change. */}
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn(
          "inline-flex items-center gap-0.5 hover:text-pb-text",
          align === "end" && "justify-self-end",
          sortKey === k ? "text-pb-text" : "text-pb-text-muted",
        )}
      >
        {label}
        {sortKey === k ? (
          asc ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : null}
      </button>
    </span>
  );

  if (data.kpis.length === 0)
    return (
      <EmptyState
        size="sm"
        title="No KPIs selected"
        description="Choose KPIs in the widget settings to build your table."
      />
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 px-2 py-2 rounded-md bg-pb-purple-soft/20 border border-pb-purple/20">
        <Sparkles className="w-3.5 h-3.5 text-pb-purple-text mt-0.5 shrink-0" />
        <p className="text-dense-1 text-pb-text-secondary leading-snug">
          {buildSummary(data.kpis)}
        </p>
      </div>

      {
        // `display:contents` wrapper carries the table semantics without a box,
        // so the CSS-grid layout (and VR baseline) is unchanged but AT gets
        // table/row/columnheader/cell structure.
        <div role="table" aria-label="KPI table" className="contents">
          <div
            role="row"
            className={cn(
              GRID,
              "px-2 pb-1.5 border-b border-pb-border/60 text-dense-2 uppercase tracking-wider font-semibold",
            )}
          >
            {sortHeader("name", "KPI")}
            {sortHeader("status", "Status", "end")}
            <span
              role="columnheader"
              className="justify-self-end text-pb-text-muted"
            >
              Value
            </span>
            {sortHeader("delta", "Δ", "end")}
          </div>
          <div role="rowgroup" className="divide-y divide-pb-border/40">
            {rows.map((k) => (
              <div
                key={k.id}
                role="row"
                className={cn(
                  GRID,
                  "px-2 py-1.5 rounded-sm transition-colors hover:pb-overlay-paint",
                )}
              >
                <span role="cell" className="min-w-0 flex items-center gap-2">
                  <span className="text-sm text-pb-text truncate">
                    {k.name}
                  </span>
                  <Sparkline
                    data={k.series}
                    width={48}
                    height={14}
                    tone={statusToSparkTone(k.status)}
                    className="shrink-0 hidden sm:block"
                    aria-hidden="true"
                  />
                </span>
                <span
                  role="cell"
                  className="justify-self-end flex items-center gap-1.5"
                >
                  <span
                    className={cn(
                      "w-2 h-2 rounded-full",
                      statusToken(k.status).dotClass,
                    )}
                  />
                  <span className="text-dense-1 text-pb-text-secondary capitalize">
                    {k.status}
                  </span>
                </span>
                <span
                  role="cell"
                  className="justify-self-end text-sm text-pb-text font-mono pb-tabular"
                >
                  {k.value}
                </span>
                <span
                  role="cell"
                  className={cn(
                    "justify-self-end text-xs font-mono pb-tabular",
                    statusDeltaClass(k.status),
                  )}
                >
                  {k.delta}
                </span>
              </div>
            ))}
          </div>
        </div>
      }
    </div>
  );
}
